import { Modal } from '.';
import { X, Zap, Scale, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import type { FC } from 'react';

interface HowToModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const steps = [
  {
    title: 'Faça upload do contrato',
    description:
      'Envie seu contrato em formato PDF ou Word(docx). Nossa plataforma aceita documentos de até 10MB.',
  },
  {
    title: 'A IA analisa seu contrato',
    description:
      'Nossa inteligência artificial examina cada cláusula, identifica riscos e verifica a conformidade legal.',
  },
  {
    title: 'Receba nota e análise completa',
    description:
      'Obtenha uma nota de 0 a 100 para seu contrato, com detalhamento dos principais problemas encontrados.',
  },
];

const criteria = [
  'Cláusulas atípicas',
  'Cláusulas padrões',
  'Objetividade, clareza e erros',
  'Equilíbrio contratual',
  'Contradição contratual',
  'Riscos financeiros',
  'Datas importantes',
];

const HowToModal: FC<HowToModalProps> = ({ isOpen, onClose }) => (
  <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl w-full !p-0">
    {/* Header */}
    <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
      <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
        Como usar o CKLawyer
      </h2>
      <button onClick={onClose} className="hover:opacity-80 transition-opacity">
        <X size={20} />
      </button>
    </div>
    {/* Content */}
    <div className="md:p-6 bg-white max-h-[80vh] overflow-y-auto rounded-b-xl">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left column */}
        <div className="md:col-span-6 flex flex-col gap-6">
          {/* Como Funciona */}
          <div className="flex items-center gap-2 font-semibold text-gray-800">
            <Zap size={20} className="text-blue-600" /> Como Funciona
          </div>
          <div className="flex flex-col gap-4">
            {steps.map((step, idx) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="border border-blue-300 rounded-xl p-4 flex gap-2 items-start bg-white shadow-sm"
              >
                <div className="w-16 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm mt-1">
                  {idx + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 leading-snug">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        {/* Right column */}
        <div className="md:col-span-6 flex flex-col gap-6">
          {/* Critérios */}
          <div>
            <div className="flex items-center gap-2 font-semibold text-gray-800">
              <Info size={20} className="text-blue-600" /> Critérios de Avaliação
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {criteria.map((crit, idx) => (
                <div
                  key={crit}
                  className="border border-gray-200 rounded-xl p-4 flex items-center gap-2 bg-white shadow-sm"
                >
                  <div className="w-8 h-8 mr-2 rounded-full bg-gray-300 text-gray-900 flex align-center items-center justify-center font-semibold text-xs">
                    {idx + 1}
                  </div>
                  <span className="text-gray-800 text-sm font-medium">{crit}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Escala de Avaliação */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 font-semibold text-gray-800 mb-4">
              <Scale size={20} className="text-blue-600" /> Escala de Avaliação
            </div>
            <ul className="space-y-1 text-sm">
              <li className="flex items-center gap-2 text-gray-800">
                <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> 0-49: Contrato
                problemático
              </li>
              <li className="flex items-center gap-2 text-gray-800">
                <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" /> 50-79: Contrato
                requer atenção
              </li>
              <li className="flex items-center gap-2 text-gray-800">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> 80-100: Contrato
                bom
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </Modal>
);

export default HowToModal;
